<template>
  <div class="import-trends mb-6">
    <div class="flex items-center justify-between mb-3">
      <span class="text-xs text-gray-500">
        Seçilen tarih aralığındaki yüklemeler
      </span>
      <span v-if="data" class="text-xs text-gray-500">
        {{ granularityLabel }} · hesaplandı: {{ data.computed_at }}
      </span>
    </div>

    <div v-if="loading" class="p-4 text-sm text-gray-500">Hesaplanıyor…</div>
    <div
      v-else-if="error"
      class="p-3 text-sm rounded-lg bg-red-50 border border-red-200 text-red-800"
    >
      {{ error }}
    </div>

    <template v-else-if="data">
      <!-- Özet -->
      <div class="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-4">
        <div
          v-for="tile in tiles"
          :key="tile.label"
          class="bg-white border rounded-lg p-3"
        >
          <div class="text-xs text-gray-500">{{ tile.label }}</div>
          <div class="text-2xl font-semibold text-gray-900">
            {{ tile.value }}
          </div>
          <div v-if="tile.hint" class="text-xs text-gray-500">
            {{ tile.hint }}
          </div>
        </div>
      </div>

      <div
        v-if="data.totals.rows === 0"
        class="p-4 text-sm text-gray-500 bg-white border rounded-lg"
      >
        Seçilen aralıkta yükleme yok.
      </div>

      <template v-else>
        <!-- Trend -->
        <div class="grid grid-cols-1 xl:grid-cols-3 gap-3 mb-4">
          <div class="bg-white border rounded-lg p-3 xl:col-span-2">
            <div class="text-sm font-medium">Yüklenen satırlar</div>
            <div class="text-xs text-gray-500 mb-1">
              Yeni eklenen ve double (sisteme eklenmeyen) satırlar
            </div>
            <apexchart
              type="bar"
              height="280"
              :options="volumeOptions"
              :series="volumeSeries"
            />
          </div>
          <div class="bg-white border rounded-lg p-3">
            <div class="text-sm font-medium">Double oranı (%)</div>
            <div class="text-xs text-gray-500 mb-1">
              Double / yüklenen satır
            </div>
            <apexchart
              type="line"
              height="280"
              :options="rateOptions"
              :series="rateSeries"
            />
          </div>
        </div>

        <div
          v-if="data.breakdown_skipped"
          class="p-3 mb-4 text-xs rounded-lg bg-yellow-50 border border-yellow-200 text-yellow-800"
        >
          Aralıkta çok fazla tekil double kişi var; kişi kırılımları
          hesaplanmadı. Daha kısa bir tarih aralığı seçin.
        </div>

        <template v-else>
          <div
            class="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3 mb-4"
          >
            <!-- Kaynak kalitesi -->
            <div class="bg-white border rounded-lg p-3 xl:col-span-2">
              <div class="text-sm font-medium">
                Kaynak bazında yükleme kalitesi
              </div>
              <div class="text-xs text-gray-500 mb-2">
                Kaynak: dosyadan eklenen kayıtların utm_source'u. Tamamı double
                olan dosyalarda kaynak belirsizdir.
              </div>
              <table class="w-full text-xs">
                <tr class="text-gray-500">
                  <th class="text-left py-1">Kaynak</th>
                  <th class="text-right">Yükleme</th>
                  <th class="text-right">Satır</th>
                  <th class="text-right">Double</th>
                  <th class="text-left pl-3 w-1/3">Double oranı</th>
                </tr>
                <tr
                  v-for="row in data.by_import_source"
                  :key="row.source"
                  class="border-t"
                >
                  <td class="py-1">{{ row.source }}</td>
                  <td class="text-right">{{ fmt(row.imports) }}</td>
                  <td class="text-right">{{ fmt(row.rows) }}</td>
                  <td class="text-right">{{ fmt(row.double) }}</td>
                  <td class="pl-3">
                    <div class="flex items-center gap-2">
                      <div class="flex-1 h-2 bg-gray-100 rounded">
                        <div
                          class="bar h-2"
                          :style="{ width: row.double_rate + '%' }"
                        ></div>
                      </div>
                      <span class="w-12 text-right"
                        >%{{ row.double_rate }}</span
                      >
                    </div>
                  </td>
                </tr>
              </table>
            </div>

            <!-- Tekrar dağılımı -->
            <div class="bg-white border rounded-lg p-3">
              <div class="text-sm font-medium">Aynı kişi kaç kez geldi</div>
              <div class="text-xs text-gray-500 mb-2">
                Tekil double kişiler, aralık içindeki geliş sayısına göre
              </div>
              <breakdown-table
                :rows="data.repeat_distribution"
                value-key="people"
                value-label="Kişi"
              />
            </div>

            <!-- Kimin kaydı -->
            <div class="bg-white border rounded-lg p-3">
              <div class="text-sm font-medium">
                Double kimin kaydına düşüyor
              </div>
              <div class="text-xs text-gray-500 mb-2">
                Mevcut kaydın sahibi (agent)
              </div>
              <breakdown-table :rows="data.by_owner" :people="true" />
            </div>

            <!-- Durum -->
            <div class="bg-white border rounded-lg p-3">
              <div class="text-sm font-medium">Mevcut kaydın durumu</div>
              <div class="text-xs text-gray-500 mb-2">
                Tekrar gelen kişi sistemde ne durumda
              </div>
              <breakdown-table :rows="data.by_status" :people="true" />
            </div>

            <!-- Yaş -->
            <div class="bg-white border rounded-lg p-3">
              <div class="text-sm font-medium">
                Kayıt ne kadar önce alınmıştı
              </div>
              <div class="text-xs text-gray-500 mb-2">
                İlk double gelişi ile mevcut kaydın oluşturulması arası
              </div>
              <breakdown-table :rows="data.by_age" :people="true" />
            </div>

            <!-- Mevcut kaynak -->
            <div class="bg-white border rounded-lg p-3">
              <div class="text-sm font-medium">Mevcut kaydın kaynağı</div>
              <div class="text-xs text-gray-500 mb-2">
                Kişi bize ilk hangi kaynaktan gelmişti
              </div>
              <breakdown-table :rows="data.by_existing_source" :people="true" />
            </div>

            <!-- Çakışma -->
            <div class="bg-white border rounded-lg p-3 lg:col-span-2">
              <div class="text-sm font-medium">Kaynaklar arası çakışma</div>
              <div class="text-xs text-gray-500 mb-2">
                Hangi kaynağın dosyası, hangi kaynaktan zaten elimizde olan
                kişileri getiriyor
              </div>
              <table class="w-full text-xs">
                <tr class="text-gray-500">
                  <th class="text-left py-1">Yükleme kaynağı</th>
                  <th class="text-left">Mevcut kaydın kaynağı</th>
                  <th class="text-right">Double satır</th>
                  <th class="text-right">Kişi</th>
                </tr>
                <tr
                  v-for="row in data.source_overlap"
                  :key="row.import_source + '|' + row.existing_source"
                  class="border-t"
                >
                  <td class="py-1">{{ row.import_source }}</td>
                  <td>{{ row.existing_source }}</td>
                  <td class="text-right">{{ fmt(row.rows) }}</td>
                  <td class="text-right">{{ fmt(row.people) }}</td>
                </tr>
              </table>
            </div>
          </div>

          <!-- En çok tekrar gelenler -->
          <div
            v-if="data.top_repeated.length"
            class="bg-white border rounded-lg p-3 mb-4"
          >
            <div class="text-sm font-medium">En çok tekrar gelen kişiler</div>
            <div class="text-xs text-gray-500 mb-2">
              Aralık içinde en az 2 kez double olarak gelenler
            </div>
            <table class="w-full text-xs">
              <tr class="text-gray-500">
                <th class="text-left py-1">Kişi</th>
                <th class="text-right">Geliş</th>
                <th class="text-right">Dosya</th>
                <th class="text-left pl-3">Sahibi</th>
                <th class="text-left">Durum</th>
                <th class="text-left">İlk kaynak</th>
                <th class="text-left">Kayıt tarihi</th>
              </tr>
              <tr
                v-for="row in data.top_repeated"
                :key="row.id"
                class="border-t"
              >
                <td class="py-1">
                  <template v-if="row.missing"
                    >#{{ row.id }} (silinmiş)</template
                  >
                  <a v-else :href="'/leadDetail/' + row.id">{{
                    row.name || "#" + row.id
                  }}</a>
                  <span
                    v-if="row.is_customer"
                    class="ml-1 px-1.5 rounded-full bg-gray-100 text-gray-700"
                    >müşteri</span
                  >
                </td>
                <td class="text-right">{{ row.times }}</td>
                <td class="text-right">{{ row.files }}</td>
                <td class="pl-3">{{ row.agent_name || "-" }}</td>
                <td>{{ row.status_name || "-" }}</td>
                <td>{{ row.utm_source || "-" }}</td>
                <td>{{ row.created_at || "-" }}</td>
              </tr>
            </table>
          </div>
        </template>

        <div v-if="notes.length" class="text-xs text-gray-500">
          <div v-for="note in notes" :key="note">{{ note }}</div>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import VueApexCharts from "vue-apexcharts";
import http from "../../services/http";

// Kategorik sıra (doğrulandı: CVD / kontrast): 1 mavi, 2 turuncu.
const SERIES_ADDED = "#2a78d6";
const SERIES_DOUBLE = "#eb6834";
const GRID = "#eceae5";
const TEXT_MUTED = "#6b6a66";

const nf = new Intl.NumberFormat("tr-TR");

// Sayı + çubuk tablosu; değerler metin renginde, çubuk tek tonda.
const BreakdownTable = {
  props: {
    rows: { type: Array, default: () => [] },
    valueKey: { type: String, default: "rows" },
    valueLabel: { type: String, default: "Double satır" },
    people: { type: Boolean, default: false }
  },
  computed: {
    max() {
      return Math.max(1, ...this.rows.map(r => r[this.valueKey] || 0));
    }
  },
  methods: {
    fmt(v) {
      return nf.format(v || 0);
    }
  },
  render(h) {
    const head = h("tr", { class: "text-gray-500" }, [
      h("th", { class: "text-left py-1" }, ""),
      h("th", { class: "text-left pl-2 w-1/3" }, ""),
      h("th", { class: "text-right" }, this.valueLabel),
      this.people ? h("th", { class: "text-right" }, "Kişi") : null
    ]);
    const body = this.rows.map(row =>
      h("tr", { key: row.key || row.label, class: "border-t" }, [
        h("td", { class: "py-1 pr-2" }, row.label),
        h("td", { class: "pl-2" }, [
          h("div", { class: "h-2 bg-gray-100 rounded" }, [
            h("div", {
              class: "bar h-2",
              style: {
                width: (100 * (row[this.valueKey] || 0)) / this.max + "%"
              }
            })
          ])
        ]),
        h("td", { class: "text-right" }, this.fmt(row[this.valueKey])),
        this.people
          ? h("td", { class: "text-right" }, this.fmt(row.people))
          : null
      ])
    );
    if (!this.rows.length) {
      return h("div", { class: "text-xs text-gray-500" }, "Veri yok");
    }
    return h("table", { class: "w-full text-xs" }, [head, ...body]);
  }
};

export default {
  name: "ImportTrends",
  components: { apexchart: VueApexCharts, BreakdownTable },
  props: {
    startDate: { type: String, required: true },
    endDate: { type: String, required: true },
    // Üst bileşen "Filtrele"ye basınca artar; tarih değişmese de yeniden çeker.
    reloadKey: { type: Number, default: 0 }
  },
  data() {
    return { data: null, loading: false, error: null, requestId: 0 };
  },
  watch: {
    reloadKey() {
      this.load();
    }
  },
  created() {
    this.load();
  },
  computed: {
    granularityLabel() {
      return this.data && this.data.range.granularity === "week"
        ? "Haftalık"
        : "Günlük";
    },
    tiles() {
      const t = this.data.totals;
      const customer = (this.data.by_customer || []).find(
        r => r.label === "Müşteri"
      );
      const tiles = [
        {
          label: "Yüklenen satır",
          value: this.fmt(t.rows),
          hint: this.fmt(t.imports) + " yükleme"
        },
        { label: "Yeni eklenen", value: this.fmt(t.added) },
        {
          label: "Double",
          value: this.fmt(t.double),
          hint: "%" + t.double_rate + " oran"
        },
        { label: "Tekil double kişi", value: this.fmt(t.double_people) },
        {
          label: "Tekrar tekrar gelen",
          value: this.fmt(t.repeat_people),
          hint: "en az 2 kez gelen kişi"
        }
      ];
      if (customer) {
        tiles.push({
          label: "Zaten müşteri olan",
          value: this.fmt(customer.people),
          hint: this.fmt(customer.rows) + " double satır"
        });
      }
      return tiles;
    },
    notes() {
      const notes = [];
      if (this.data.totals.unparsed_double > 0) {
        notes.push(
          this.fmt(this.data.totals.unparsed_double) +
            " double satırın eşleştiği kayıt listesi raporda kırpılmış; kişi kırılımlarına girmedi."
        );
      }
      if (this.data.missing_rows > 0) {
        notes.push(
          this.fmt(this.data.missing_rows) +
            " double satırın eşleştiği mevcut kayıt sonradan silinmiş."
        );
      }
      return notes;
    },
    categories() {
      return this.data.trend.map(b => b.date);
    },
    volumeSeries() {
      return [
        { name: "Yeni eklenen", data: this.data.trend.map(b => b.added) },
        { name: "Double", data: this.data.trend.map(b => b.double) }
      ];
    },
    volumeOptions() {
      const trend = this.data.trend;
      return {
        chart: {
          type: "bar",
          stacked: true,
          toolbar: { show: false },
          fontFamily: "inherit"
        },
        colors: [SERIES_ADDED, SERIES_DOUBLE],
        plotOptions: {
          bar: {
            columnWidth: trend.length > 20 ? "70%" : "45%",
            borderRadius: 4,
            borderRadiusApplication: "end",
            borderRadiusWhenStacked: "last"
          }
        },
        // Yığın parçaları arasında 2px yüzey boşluğu
        stroke: { show: true, width: 2, colors: ["#ffffff"] },
        dataLabels: { enabled: false },
        legend: {
          position: "top",
          horizontalAlign: "left",
          labels: { colors: TEXT_MUTED }
        },
        grid: { borderColor: GRID, strokeDashArray: 0 },
        xaxis: {
          categories: this.categories,
          labels: {
            style: { colors: TEXT_MUTED },
            rotate: -45,
            hideOverlappingLabels: true,
            formatter: v => this.shortDate(v)
          },
          axisBorder: { color: GRID },
          axisTicks: { show: false }
        },
        yaxis: {
          labels: {
            style: { colors: TEXT_MUTED },
            formatter: v => this.compact(v)
          }
        },
        tooltip: {
          shared: true,
          intersect: false,
          y: { formatter: v => this.fmt(v) },
          // Başlıkta o günün double oranı da görünsün (tek eksen; oran ayrı grafikte).
          x: {
            formatter: (v, { dataPointIndex }) => {
              const b = trend[dataPointIndex];
              return b
                ? b.date +
                    " · double %" +
                    b.double_rate +
                    " · " +
                    b.imports +
                    " yükleme"
                : v;
            }
          }
        }
      };
    },
    rateSeries() {
      return [
        { name: "Double oranı", data: this.data.trend.map(b => b.double_rate) }
      ];
    },
    rateOptions() {
      return {
        chart: {
          type: "line",
          toolbar: { show: false },
          zoom: { enabled: false },
          fontFamily: "inherit"
        },
        colors: [SERIES_DOUBLE],
        stroke: { width: 2, curve: "straight", lineCap: "round" },
        markers: {
          size: this.data.trend.length > 31 ? 0 : 4,
          strokeWidth: 2,
          strokeColors: "#ffffff"
        },
        dataLabels: { enabled: false },
        legend: { show: false },
        grid: { borderColor: GRID, strokeDashArray: 0 },
        xaxis: {
          categories: this.categories,
          labels: {
            style: { colors: TEXT_MUTED },
            rotate: -45,
            hideOverlappingLabels: true,
            formatter: v => this.shortDate(v)
          },
          axisBorder: { color: GRID },
          axisTicks: { show: false },
          tooltip: { enabled: false }
        },
        yaxis: {
          min: 0,
          max: 100,
          tickAmount: 4,
          labels: {
            style: { colors: TEXT_MUTED },
            formatter: v => "%" + Math.round(v)
          }
        },
        tooltip: {
          x: {
            formatter: (v, { dataPointIndex }) =>
              this.categories[dataPointIndex] || v
          },
          y: { formatter: v => "%" + v }
        }
      };
    }
  },
  methods: {
    load() {
      const requestId = ++this.requestId;
      this.loading = true;
      this.error = null;
      http
        .getImportTrends(this.startDate, this.endDate)
        .then(resp => {
          if (requestId !== this.requestId) return;
          this.data = resp.data;
        })
        .catch(err => {
          if (requestId !== this.requestId) return;
          this.data = null;
          this.error =
            (err.response && err.response.data && err.response.data.message) ||
            "Trend raporu alınamadı.";
        })
        .finally(() => {
          if (requestId === this.requestId) this.loading = false;
        });
    },
    fmt(v) {
      return nf.format(v || 0);
    },
    // Eksende "27.09"; tam tarih ipucunda.
    shortDate(v) {
      const s = String(v || "");
      return /^\d{4}-\d{2}-\d{2}$/.test(s)
        ? s.slice(8, 10) + "." + s.slice(5, 7)
        : s;
    },
    compact(v) {
      if (v >= 1000000) return Math.round(v / 100000) / 10 + "M";
      if (v >= 1000) return Math.round(v / 100) / 10 + "K";
      return Math.round(v);
    }
  }
};
</script>

<style scoped>
/* Alt bileşen (BreakdownTable) dahil tüm oran çubukları; tek ton, uç yuvarlak. */
.import-trends >>> .bar {
  background: #2a78d6;
  border-radius: 0 4px 4px 0;
}
</style>
